import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { BUSINESS_TYPES } from "@/lib/businessTypes";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FloppyDisk, Trash, ChatText } from "@phosphor-icons/react";

const PLACEHOLDERS = ["{customer_name}", "{business_name}", "{business_type}", "{next_date}", "{next_km}", "{phone}"];

const SAMPLE_TEMPLATE =
  "Hi {customer_name}, this is a reminder from {business_name}: your {business_type} service is due on {next_date}. Please book your appointment.";

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [bizType, setBizType] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/templates");
    setTemplates(data);
  };
  useEffect(() => { load(); }, []);

  const loadOne = (b) => {
    setBizType(b);
    const existing = templates.find((t) => t.business_type === b);
    setBody(existing?.template || SAMPLE_TEMPLATE);
  };

  const save = async () => {
    if (!bizType) { toast.error("Pick a business type"); return; }
    if (!body.trim()) { toast.error("Template body cannot be empty"); return; }
    setSaving(true);
    try {
      await api.put("/templates", { business_type: bizType, template: body });
      toast.success(`Template saved for ${bizType}`);
      load();
    } catch {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!bizType) return;
    if (!window.confirm(`Remove template for ${bizType}?`)) return;
    await api.delete(`/templates/${encodeURIComponent(bizType)}`);
    toast.success("Removed");
    setBody(SAMPLE_TEMPLATE);
    load();
  };

  const insertPlaceholder = (p) => setBody((b) => b + (b.endsWith(" ") || b === "" ? "" : " ") + p);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4A4A4A]">Message authoring</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1">Templates</h1>
        <p className="text-sm text-[#4A4A4A] font-semibold mt-1">One template per business type. Used for manual sends and the daily 9 AM IST digest.</p>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        <div className="nb-card p-3 space-y-1 h-fit">
          <p className="px-2 py-1 text-xs font-bold tracking-[0.15em] uppercase">Business Types</p>
          {BUSINESS_TYPES.map((b) => {
            const has = templates.some((t) => t.business_type === b);
            return (
              <button
                key={b}
                data-testid={`tpl-pick-${b.replace(/\s+/g,'-')}`}
                onClick={() => loadOne(b)}
                className={`w-full text-left px-3 py-2 rounded-md font-bold text-sm flex items-center justify-between ${bizType === b ? "bg-[#111111] text-white" : "hover:bg-[#F3F0E9]"}`}
              >
                <span className="truncate">{b}</span>
                {has && <span className="ml-2 text-[10px] font-black px-1.5 py-0.5 bg-[#06D6A0] border-2 border-black rounded-full">SET</span>}
              </button>
            );
          })}
        </div>

        <div className="nb-card p-6 space-y-4">
          {!bizType ? (
            <div className="py-16 text-center">
              <ChatText size={48} weight="bold" className="mx-auto mb-3 text-[#4A4A4A]" />
              <p className="font-bold">Pick a business type to edit its template.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-2xl font-black">{bizType}</h2>
              </div>
              <div>
                <Label className="text-xs font-bold tracking-[0.15em] uppercase mb-1.5 block">Message body</Label>
                <Textarea data-testid="tpl-body" value={body} onChange={(e) => setBody(e.target.value)} className="nb-input min-h-[160px]" />
              </div>
              <div className="flex flex-wrap gap-2">
                {PLACEHOLDERS.map((p) => (
                  <button
                    key={p}
                    data-testid={`tpl-ph-${p}`}
                    onClick={() => insertPlaceholder(p)}
                    type="button"
                    className="text-xs font-mono font-bold border-2 border-black rounded-full px-2.5 py-1 bg-[#FFD166] hover:bg-[#FF6B6B]"
                  >
                    {p}
                  </button>
                ))}
              </div>
              <div className="flex gap-3 pt-2">
                <button data-testid="tpl-save" onClick={save} disabled={saving} className="nb-btn bg-[#4361EE] text-white px-5 py-2.5 flex items-center gap-2">
                  <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save Template"}
                </button>
                <button data-testid="tpl-delete" onClick={remove} className="nb-btn bg-white px-5 py-2.5 flex items-center gap-2">
                  <Trash size={16} weight="bold" /> Reset to default
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
